import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropZone", 
    "fileInput", 
    "errorContainer", 
    "newImagesPreview", 
    "existingImagesContainer",
    "primaryCheckbox",
    "destroyCheckbox",
    "form"
  ]

  connect() {
    this.uploadedFiles = []
    
    // Try to find the form and set up the form submission handler
    this.setupFormHandler()
  }
  
  // Set up the form handler
  setupFormHandler() {
    let form
    
    // If we have a form target already connected, use it
    if (this.hasFormTarget) {
      form = this.formTarget
    } else {
      // Otherwise, try to find the closest form
      form = this.element.closest('form')
      if (form) {
        // Mark it as our form target
        form.setAttribute('data-image-upload-target', 'form')
      } else {
        console.warn("No form found for image-upload controller")
        return // Exit early if no form found
      }
    }
    
    // Add the submit handler
    form.addEventListener('submit', this.handleFormSubmit.bind(this))
  }

  // Handle form submission with our custom logic
  handleFormSubmit(event) {
    // No need to continue if we don't have any uploaded files
    if (!this.uploadedFiles || this.uploadedFiles.length === 0) {
      return // Let the form submit normally
    }
    
    // Prevent the default form submission
    event.preventDefault()
    
    // Make sure we have access to the form
    const form = event.target || this.element.closest('form')
    if (!form) {
      console.error("Cannot find form to submit")
      return
    }
    
    // Create FormData from the form
    const formData = new FormData(form)
    
    // Find which uploaded file is primary (if any)
    const primaryIndex = this.uploadedFiles.findIndex(file => file.isPrimary)
    
    // Append each uploaded file to the FormData
    this.uploadedFiles.forEach((file, index) => {
      formData.append('images[]', file)
    })
    
    // If we have a primary image, add that info
    if (primaryIndex >= 0) {
      formData.append('primary_image_index', primaryIndex)
    }
    
    // Submit using fetch
    fetch(form.action, {
      method: form.method || 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html,application/xhtml+xml,application/xml'
      },
      redirect: 'follow'
    })
    .then(response => {
      if (response.redirected) {
        window.location.href = response.url
      } else {
        return response.text().then(html => {
          document.open()
          document.write(html)
          document.close()
        })
      }
    })
    .catch(error => {
      console.error('Error submitting form:', error)
      // Fall back to normal form submission if AJAX fails
      this.submitFormNormally(form)
    })
  }
  
  // Helper function to submit the form normally (without AJAX)
  submitFormNormally(originalForm) {
    // Use the original form or find it
    const form = originalForm || this.element.closest('form')
    if (!form) {
      console.error("Cannot find form to submit normally")
      return
    }
    
    // Create a standard form submission
    const submitForm = document.createElement('form')
    submitForm.method = form.method || 'POST'
    submitForm.action = form.action
    submitForm.enctype = 'multipart/form-data'
    
    // Clone all form fields except file inputs
    Array.from(form.elements).forEach(element => {
      if (element.tagName !== 'INPUT' || element.type !== 'file') {
        const clone = element.cloneNode(true)
        submitForm.appendChild(clone)
      }
    })
    
    // Add files to the submission
    this.uploadedFiles.forEach(file => {
      const fileInput = document.createElement('input')
      fileInput.type = 'file'
      fileInput.name = 'images[]'
      fileInput.style.display = 'none'
      
      // Use the DataTransfer API to try to set the files
      const dt = new DataTransfer()
      dt.items.add(file)
      fileInput.files = dt.files
      
      submitForm.appendChild(fileInput)
    })
    
    // Add primary image index if needed
    const primaryIndex = this.uploadedFiles.findIndex(file => file.isPrimary)
    if (primaryIndex >= 0) {
      const primaryField = document.createElement('input')
      primaryField.type = 'hidden'
      primaryField.name = 'primary_image_index'
      primaryField.value = primaryIndex
      submitForm.appendChild(primaryField)
    }
    
    // Submit the new form
    document.body.appendChild(submitForm)
    submitForm.submit()
    document.body.removeChild(submitForm)
  }

  // Create a FileList-like object from an array of files
  createFileList(files) {
    const dataTransfer = new DataTransfer()
    files.forEach(file => dataTransfer.items.add(file))
    return dataTransfer.files
  }

  // Open file dialog when clicking on the drop zone
  openFileDialog(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  // Prevent default behavior for drag events
  preventDefault(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  // Handle file drop
  onFileDrop(event) {
    this.preventDefault(event)
    const files = event.dataTransfer.files
    this.handleFiles(files)
  }

  // Handle file selection from dialog
  onFileSelected(event) {
    const files = event.target.files
    this.handleFiles(files)
  }

  // Process the files
  handleFiles(files) {
    if (!files || files.length === 0) return

    // Reset error messages
    this.errorContainerTarget.innerHTML = ''
    this.errorContainerTarget.classList.add('hidden')
    
    let hasErrors = false
    const maxFileSize = 5 * 1024 * 1024 // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    
    // Validate files
    Array.from(files).forEach(file => {
      // Check file size
      if (file.size > maxFileSize) {
        this.addError(`File "${file.name}" exceeds the maximum size of 5MB`)
        hasErrors = true
      }
      
      // Check file type
      if (!allowedTypes.includes(file.type)) {
        this.addError(`File "${file.name}" is not a supported image format (JPG, PNG, GIF, WEBP)`)
        hasErrors = true
      }
    })
    
    if (hasErrors) {
      this.errorContainerTarget.classList.remove('hidden')
      return
    }

    // Process valid files
    Array.from(files).forEach(file => {
      this.previewFile(file)
      this.uploadedFiles.push(file)
    })

    // Reset the file input to allow selecting the same file again
    this.fileInputTarget.value = ''
  }

  // Add preview for the file
  previewFile(file) {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      const previewId = `preview-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
      
      // Create preview container
      const previewContainer = document.createElement('div')
      previewContainer.className = 'relative group'
      previewContainer.dataset.previewId = previewId
      
      // Create image container
      const imageContainer = document.createElement('div')
      imageContainer.className = 'overflow-hidden border border-gray-200 rounded-lg aspect-square dark:border-gray-700'
      
      // Create image
      const image = document.createElement('img')
      image.src = e.target.result
      image.className = 'object-cover w-full h-full'
      image.alt = file.name
      
      // Create actions overlay
      const actionsOverlay = document.createElement('div')
      actionsOverlay.className = 'absolute inset-0 flex items-center justify-center transition-opacity opacity-0 bg-black/50 group-hover:opacity-100'
      
      // Create actions container
      const actionsContainer = document.createElement('div')
      actionsContainer.className = 'flex flex-col items-center gap-2'
      
      // Create primary button
      const primaryButton = document.createElement('button')
      primaryButton.type = 'button'
      primaryButton.className = 'p-2 text-white bg-green-600 rounded-full hover:bg-green-700'
      primaryButton.dataset.action = 'click->image-upload#setNewPrimaryImage'
      primaryButton.dataset.previewId = previewId
      
      const primaryIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      primaryIcon.setAttribute('class', 'w-5 h-5')
      primaryIcon.setAttribute('fill', 'none')
      primaryIcon.setAttribute('viewBox', '0 0 24 24')
      primaryIcon.setAttribute('stroke', 'currentColor')
      
      const primaryPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      primaryPath.setAttribute('stroke-linecap', 'round')
      primaryPath.setAttribute('stroke-linejoin', 'round')
      primaryPath.setAttribute('stroke-width', '2')
      primaryPath.setAttribute('d', 'M5 13l4 4L19 7')
      
      primaryIcon.appendChild(primaryPath)
      primaryButton.appendChild(primaryIcon)
      
      // Create remove button
      const removeButton = document.createElement('button')
      removeButton.type = 'button'
      removeButton.className = 'p-2 text-white bg-red-600 rounded-full hover:bg-red-700'
      removeButton.dataset.action = 'click->image-upload#removeNewImage'
      removeButton.dataset.previewId = previewId
      
      const removeIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      removeIcon.setAttribute('class', 'w-5 h-5')
      removeIcon.setAttribute('fill', 'none')
      removeIcon.setAttribute('viewBox', '0 0 24 24')
      removeIcon.setAttribute('stroke', 'currentColor')
      
      const removePath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      removePath.setAttribute('stroke-linecap', 'round')
      removePath.setAttribute('stroke-linejoin', 'round')
      removePath.setAttribute('stroke-width', '2')
      removePath.setAttribute('d', 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16')
      
      removeIcon.appendChild(removePath)
      removeButton.appendChild(removeIcon)
      
      // Add buttons to actions container
      actionsContainer.appendChild(primaryButton)
      actionsContainer.appendChild(removeButton)
      
      // Add actions container to overlay
      actionsOverlay.appendChild(actionsContainer)
      
      // Add all elements to the preview container
      imageContainer.appendChild(image)
      previewContainer.appendChild(imageContainer)
      previewContainer.appendChild(actionsOverlay)
      
      // Add to DOM
      this.newImagesPreviewTarget.appendChild(previewContainer)
      
      // Store the file reference in the uploadedFiles array with the previewId
      file.previewId = previewId
    }
    
    reader.readAsDataURL(file)
  }

  // Set a new image as primary
  setNewPrimaryImage(event) {
    event.preventDefault()
    
    const previewId = event.currentTarget.dataset.previewId
    
    // Remove primary indicator from all new images
    this.newImagesPreviewTarget.querySelectorAll('.primary-indicator').forEach(indicator => {
      indicator.remove()
    })
    
    // Create and add primary indicator to this image
    const previewContainer = this.newImagesPreviewTarget.querySelector(`[data-preview-id="${previewId}"]`)
    const primaryIndicator = document.createElement('div')
    primaryIndicator.className = 'absolute top-2 right-2 primary-indicator'
    
    const primaryBadge = document.createElement('span')
    primaryBadge.className = 'px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-full'
    primaryBadge.textContent = 'Primary'
    
    primaryIndicator.appendChild(primaryBadge)
    previewContainer.appendChild(primaryIndicator)
    
    // Mark this file as primary in the uploadedFiles array
    // First, reset all files to non-primary
    this.uploadedFiles.forEach(file => {
      file.isPrimary = false
    })
    
    // Find the specific file for this preview and mark it as primary
    const fileToMakePrimary = this.uploadedFiles.find(file => file.previewId === previewId)
    if (fileToMakePrimary) {
      fileToMakePrimary.isPrimary = true
    }

    // Also uncheck all existing primary checkboxes if we're setting a new image as primary
    if (this.hasPrimaryCheckboxTarget) {
      this.primaryCheckboxTargets.forEach(checkbox => {
        checkbox.checked = false
      })
    }
  }

  // Set an existing image as primary
  setPrimaryImage(event) {
    event.preventDefault()
    
    const imageId = event.currentTarget.dataset.imageId
    
    // Uncheck all primary checkboxes
    this.primaryCheckboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })
    
    // Check the selected primary checkbox
    const checkbox = this.primaryCheckboxTargets.find(cb => cb.dataset.imageId === imageId)
    if (checkbox) {
      checkbox.checked = true
    }
    
    // Update UI to show primary indicators
    this.existingImagesContainerTarget.querySelectorAll('.primary-indicator').forEach(indicator => {
      indicator.remove()
    })
    
    // Add primary indicator
    const imageContainer = this.existingImagesContainerTarget.querySelector(`[data-image-id="${imageId}"]`)
    if (!imageContainer.querySelector('.primary-indicator')) {
      const primaryIndicator = document.createElement('div')
      primaryIndicator.className = 'absolute top-2 right-2 primary-indicator'
      
      const primaryBadge = document.createElement('span')
      primaryBadge.className = 'px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-full'
      primaryBadge.textContent = 'Primary'
      
      primaryIndicator.appendChild(primaryBadge)
      imageContainer.appendChild(primaryIndicator)
    }
    
    // Also reset primary status for all new images
    this.newImagesPreviewTarget.querySelectorAll('.primary-indicator').forEach(indicator => {
      indicator.remove()
    })
    this.uploadedFiles.forEach(file => {
      file.isPrimary = false
    })
  }

  // Remove a new image
  removeNewImage(event) {
    event.preventDefault()
    
    const previewId = event.currentTarget.dataset.previewId
    
    // Remove from DOM
    const previewContainer = this.newImagesPreviewTarget.querySelector(`[data-preview-id="${previewId}"]`)
    if (previewContainer) {
      previewContainer.remove()
    }
    
    // Remove from uploadedFiles array
    const fileIndex = this.uploadedFiles.findIndex(file => file.previewId === previewId)
    if (fileIndex !== -1) {
      this.uploadedFiles.splice(fileIndex, 1)
    }
  }

  // Mark an existing image for deletion
  removeExistingImage(event) {
    event.preventDefault()
    
    const imageId = event.currentTarget.dataset.imageId
    
    // Mark for deletion in the form
    const checkbox = this.destroyCheckboxTargets.find(cb => cb.dataset.imageId === imageId)
    if (checkbox) {
      checkbox.checked = true
    }
    
    // Hide in UI
    const imageContainer = this.existingImagesContainerTarget.querySelector(`[data-image-id="${imageId}"]`)
    if (imageContainer) {
      imageContainer.classList.add('opacity-50')
      
      // Add "removed" indicator
      if (!imageContainer.querySelector('.removed-indicator')) {
        const removedIndicator = document.createElement('div')
        removedIndicator.className = 'absolute top-2 left-2 removed-indicator'
        
        const removedBadge = document.createElement('span')
        removedBadge.className = 'px-2 py-1 text-xs font-medium text-white bg-red-600 rounded-full'
        removedBadge.textContent = 'To be removed'
        
        removedIndicator.appendChild(removedBadge)
        imageContainer.appendChild(removedIndicator)
      }
    }
  }

  // Add error message
  addError(message) {
    const errorElement = document.createElement('div')
    errorElement.textContent = message
    this.errorContainerTarget.appendChild(errorElement)
  }
} 
